import * as React from "react";
import { useLocation, useNavigate } from "react-router";
import { useWindowManager } from "./WindowManagerContext";
import { useResponsive } from "../../hooks/useResponsive";
import WindowConfirmDialog from "./WindowConfirmDialog";

/** Liest den monotonen react-router-History-Index (window.history.state.idx). */
function readHistoryIdx(): number {
  if (typeof window === "undefined") return 0;
  const idx = (window.history.state as { idx?: unknown } | null)?.idx;
  return typeof idx === "number" ? idx : 0;
}

/**
 * Fragt beim Routen-/Menüwechsel nach, wenn ein „bildschirmfüllendes" Fenster
 * offen ist:
 *   - Verwerfen         → betroffene Fenster schließen (Form-State weg)
 *   - Minimieren        → betroffene Fenster in die Taskbar
 *   - Zurück zum Fenster → auf die Fenster-Route zurück (Navigation rückgängig)
 *
 * WAS „bildschirmfüllend" heißt, hängt vom Viewport ab:
 *   - Desktop: nur MAXIMIERTE (fullpage) Fenster. FLOATING Fenster lösen
 *     bewusst nichts aus — sie bleiben offen und schweben über der neuen Seite
 *     (freie Navigation „mit Fenster").
 *   - Phone/Tablet (≤1024px): der WindowLayer rendert JEDES Fenster als
 *     Vollbild-Panel; „floating vs. maximiert" ist dort bedeutungslos → jedes
 *     sichtbare (nicht minimierte) Fenster löst aus, sonst bliebe beim
 *     Wegnavigieren ein verwaistes Vollbild-Panel ohne Warnung stehen.
 * Minimierte Fenster lösen nie aus.
 *
 * REAKTIV: feuert nach dem (bereits erfolgten) Routenwechsel. „Zurück zum
 * Fenster" macht ihn per navigate(-delta) rückgängig, wobei `delta` aus dem
 * History-Index-Sprung stammt: PUSH(+1)→navigate(-1), Browser-Zurück(-1)→
 * navigate(+1). Der Index wird bei JEDER Navigation (auch Same-Path-PUSH, z. B.
 * Query-Wechsel) fortgeschrieben, damit `delta` nicht nachläuft.
 */
const WindowNavigationGuard: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isPhoneOrTablet } = useResponsive();
  const { windows, minimizeWindow, closeWindow } = useWindowManager();

  // Auf Mobile ist jedes sichtbare Fenster faktisch Vollbild → jedes triggert;
  // auf Desktop maximierte (fullpage) Fenster UND Vollseiten: eine Vollseite
  // belegt die ganze Content-Fläche und steht nicht im Dock — ohne Rückfrage
  // bliebe sie unsichtbar hinter der neuen Seite hängen.
  // Ein ausgelagertes Fenster (eigenes Browserfenster) verdeckt keine Seite.
  const isBlocking = React.useCallback(
    (w: { state: string; browserFenster?: boolean }) =>
      !w.browserFenster &&
      (isPhoneOrTablet ? w.state !== "minimized" : w.state === "maximized" || w.state === "page"),
    [isPhoneOrTablet],
  );

  const prevPathRef = React.useRef<string>(location.pathname);
  const prevIdxRef = React.useRef<number>(readHistoryIdx());
  const suppressRef = React.useRef(false);
  // Richtung des auslösenden Wechsels (idx-Delta) für „Zurück zum Fenster".
  const deltaRef = React.useRef(0);
  const [pending, setPending] = React.useState(false);

  React.useEffect(() => {
    const nextIdx = readHistoryIdx();
    const delta = nextIdx - prevIdxRef.current;
    const pathChanged = location.pathname !== prevPathRef.current;
    // Baselines IMMER fortschreiben (auch bei Same-Path-PUSH), sonst läuft der
    // Index-Delta bei „Zurück zum Fenster" nach.
    prevIdxRef.current = nextIdx;
    prevPathRef.current = location.pathname;
    // Der Rücksprung aus „Zurück zum Fenster" darf nicht erneut triggern.
    if (suppressRef.current) {
      suppressRef.current = false;
      return;
    }
    if (!pathChanged) return; // Same-Path: nur Baseline aktualisiert
    if (windows.some((w) => isBlocking(w))) {
      deltaRef.current = delta;
      setPending(true);
    }
  }, [location.key, location.pathname, windows, isBlocking]);

  const handleDiscard = () => {
    for (const w of windows) {
      if (isBlocking(w)) closeWindow(w.id);
    }
    setPending(false);
  };

  const handleMinimize = () => {
    for (const w of windows) {
      if (isBlocking(w)) minimizeWindow(w.id);
    }
    setPending(false);
  };

  const handleCancel = () => {
    setPending(false);
    const delta = deltaRef.current;
    if (delta === 0) return; // z.B. replace-Navigation: nichts rückgängig zu machen
    suppressRef.current = true;
    navigate(-delta); // PUSH(+1)→-1; Browser-Zurück(-1)→+1; robust in beide Richtungen
  };

  // Sprache an die Darstellung anpassen: was der User gerade verlässt, ist im
  // Vollseiten-Modus kein „Fenster", sondern das Formular selbst.
  const blocksPage = windows.some((w) => w.state === "page" && isBlocking(w));

  return (
    <WindowConfirmDialog
      open={pending}
      title="Seite gewechselt"
      message={
        blocksPage
          ? "Du hast die Seite gewechselt, während das Formular noch offen war. Verwerfen, für später minimieren, oder zurück zum Formular?"
          : "Du hast die Seite gewechselt, während noch ein Fenster offen war. Verwerfen, für später minimieren, oder zurück zum Fenster?"
      }
      discardLabel="Verwerfen"
      minimizeLabel="Minimieren"
      cancelLabel={blocksPage ? "Zurück zum Formular" : "Zurück zum Fenster"}
      onDiscard={handleDiscard}
      onMinimize={handleMinimize}
      onCancel={handleCancel}
    />
  );
};

export default WindowNavigationGuard;
